<template>
  <modal v-bind="[$props, $attrs]" @modal-close="emitCloseEvent">
    <transition name="fade" mode="out-in">
      <base-inline-loader v-if="$props.loading" height="428" :key="'loader'"/>
      <div v-else :key="'form'">
        <div class="change-address-header">
          <h2 class="title">{{ $t('billsDelivery.changeHomeAddress') }}</h2>
          <p class="subtitle" v-html="$t('billsDelivery.changeHomeAddressDescription')"></p>
        </div>
        <validation-observer ref="homeObs"
                             tag="form"
                             class="home-address-form"
                             v-autofocus
                             @submit.prevent="submitHomeAddressChange">
          <div class="form-row">
            <base-v-input v-model="streetName"
                          id="homeAddress"
                          ref="homeAddress"
                          class="input-field"
                          name="homeAddress"
                          :submitted="homeAddressSubmitted"
                          :label="$t('billsDelivery.streetModalLabel')"
                          rules="required|max:50"
                          maxlength="50"
                          vid="address">
            </base-v-input>
            <base-v-input v-model="streetNumber"
                          id="addressNumber"
                          ref="addressNumber"
                          class="input-field"
                          name="addressNumber"
                          :submitted="homeAddressSubmitted"
                          :label="$t('billsDelivery.numberModalLabel')"
                          rules="required|max:4"
                          maxlength="4"
                          vid="addressNumber">
            </base-v-input>
          </div>
          <div class="form-row">
            <div class="custom-dropdown light-label limited-dropdown">
              <label class="label">{{ $t('billsDelivery.townModalLabel') }}</label>
              <validation-provider rules="required"
                                   tag="div"
                                   :mode="homeAddressSubmitted ? 'eager' : 'passive'"
                                   v-slot="{ errors }">
                <v-select class="select-dropdown dark-background"
                          required
                          key="cities"
                          v-model="selectedCity"
                          :options="$props.cityList"
                          :reduce="option => option.name"
                          label="name"></v-select>
                <p class="error-msg" v-if="errors.length">{{ errors[0] }}</p>
              </validation-provider>
            </div>
            <div class="custom-dropdown light-label limited-dropdown">
              <label class="label">{{ $t('billsDelivery.postalCodeLabel') }}</label>
              <validation-provider rules="required"
                                   tag="div"
                                   :mode="homeAddressSubmitted ? 'eager' : 'passive'"
                                   v-slot="{ errors }">
                <v-select class="select-dropdown dark-background"
                          required
                          :searchable="false"
                          :disabled="!postcodeOptions.length"
                          v-model="postalCode"
                          :options="postcodeOptions"></v-select>
                <p class="error-msg" v-if="errors.length">{{ errors[0] }}</p>
              </validation-provider>
            </div>
          </div>
          <base-button :disabled="!addressFilled || buttonDisabled" class="save-btn" type="submit">
            {{ $t('billsDelivery.saveBtn') }}
          </base-button>
        </validation-observer>
      </div>
    </transition>
  </modal>
</template>

<script>
import Modal from '@/components/common/modal/modal'

export default {
  name: 'bills-change-address-modal',
  components: { Modal },
  inheritAttrs: false,
  props: {
    loading: Boolean,
    cityList: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data () {
    return {
      paymentAddress: {
        town: '',
        postalCode: '',
        line1: '',
        line2: ''
      },
      streetName: '',
      streetNumber: '',
      postalCode: '',
      postcodeOptions: [],
      selectedCity: '',
      buttonDisabled: false,
      homeAddressSubmitted: false
    }
  },
  computed: {
    addressFilled () {
      return !!this.streetName && !!this.streetNumber && !!this.postalCode && Object.keys(this.selectedCity).length > 1
    }
  },
  methods: {
    emitCloseEvent () {
      this.resetData()
      this.$emit('close')
    },
    async submitHomeAddressChange () {
      const formValid = await this.$refs.homeObs.validate()
      this.homeAddressSubmitted = true
      if (formValid) {
        const paymentAddress = {
          town: this.selectedCity,
          postalCode: this.postalCode,
          line1: this.streetName,
          line2: this.streetNumber
        }

        this.$emit('submitted', paymentAddress)
      }
    },
    resetData () {
      this.paymentAddress.town = ''
      this.paymentAddress.postalCode = ''
      this.paymentAddress.line1 = ''
      this.paymentAddress.line2 = ''
      this.streetName = ''
      this.streetNumber = ''
      this.postalCode = ''
      this.selectedCity = ''
    }
  },
  watch: {
    selectedCity: {
      handler (newVal) {
        if (newVal) {
          const selectedCityObj = this.$props.cityList.filter(city => city.name === newVal)

          if (selectedCityObj.length) {
            this.postcodeOptions = selectedCityObj[0].postalCodes
            this.postalCode = selectedCityObj[0].postalCodes[0]
          } else {
            this.postcodeOptions = []
            this.postalCode = ''
          }
        } else {
          this.postcodeOptions = []
          this.postalCode = ''
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.change-address-header {
  text-align: center;

  .title {
    font-size: 2.4rem;
    color: $text;
    margin-bottom: 1.6rem;
    font-weight: normal;

    @media (map_get($breakpoints, sm)) {
      font-size: 3.2rem;
    }
  }

  .subtitle {
    margin: 2.4rem 0 4rem;
    font-size: 1.4rem;

    @media (map_get($breakpoints, sm)) {
      font-size: 1.6rem;
    }
  }
}

.home-address-form {
  text-align: center;
  margin: 2rem 0 0;

  .form-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    @media (map_get($breakpoints, sm)) {
      flex-direction: row;
      max-width: 500px;
      margin: auto;
    }

    .custom-dropdown {
      padding-bottom: 3rem;
      text-align: left;
    }

    .input-field, .custom-dropdown {
      flex: 1 1 auto;
      width: 100%;
      position: relative;

      @media (map_get($breakpoints, sm)) {
        margin: 0 0.8rem 0;
      }

      .error-msg {
        position: absolute;
        top: 78px;
        left: 0;
        text-align: left;
        font-size: 1.2rem;
        line-height: 1;
        color: $primary;
      }
    }
  }

  .save-btn {
    margin-top: 1rem;
  }
}
</style>
